// AdditionalQueryPJ.jsx — versão corporativa, compacta, sem provedores
import { Checkbox, Label, Modal, ModalBody, ModalHeader } from "flowbite-react";
import { useQuery } from "react-query";
import { QUERIES } from "../../../config/apiConfig";
import axios from "../../../config/axiosInstance";
import { useState, useEffect, useMemo } from "react";
import { AiOutlineQuestionCircle } from "react-icons/ai";

/* Labels + descrições curtas (sem provedor) */
const defs = {
  // principais (títulos das colunas)
  "additional-avalia-max-pj": {
    label: "Avalia Max PJ",
    description: "Visão ampla e enriquecida para análise PJ.",
  },
  "additional-avalia-risco-pj": {
    label: "Avalia Risco PJ",
    description: "Foco em risco, inadimplência e restrições.",
  },

  // insumos compartilhados (ficam listados em Max/Risco)
  "additional-avalia-cadastral-pj": {
    label: "Cadastral",
    description: "Dados PJ consolidados e verificados.",
  },
  "additional-cadastral-pj-plus": {
    label: "Cadastral Plus",
    description: "Cadastral com fontes adicionais.",
  },
  "additional-avalia-score-positivo-pj": {
    label: "Score Positivo",
    description: "Indicadores positivos de comportamento.",
  },
  "additional-avalia-faturamento-presumido-pj": {
    label: "Renda Presumida",
    description: "Estimativa baseada em sinais de mercado.",
  },
  "additional-avalia-faturamento-presumido-pj-plus": {
    label: "Renda Presumida Plus",
    description: "Estimativa com cobertura ampliada.",
  },
  "additional-avalia-capacidade-pagamento-pj": {
    label: "Capacidade de Pagamento",
    description: "Aptidão para honrar compromissos.",
  },
  "additional-avalia-protesto-nacional-pj": {
    label: "Protesto Nacional",
    description: "Cobertura de protestos PJ.",
  },
  "additional-avalia-historico-transacionado-pj": {
    label: "Histórico Transacionado",
    description: "Contexto de movimentações históricas.",
  },
  "additional-avalia-liquidez-pj": {
    label: "Liquidez",
    description: "Liquidez geral das operações.",
  },

  // blocos financeiros (ids 35–42)
  "additional-valor-historico-transacionados-pagos-boletos-pj": {
    label: "Pagamentos - Boleto",
    description: "Totais pagos por boleto, últimos meses.",
  },
  "additional-valor-historico-transacionados-pagos-boletos-ted-pj": {
    label: "Pagamentos - Boleto / TED",
    description: "Totais pagos por boleto e TED, últimos meses.",
  },
  "additional-valor-historico-transacionado-recebidos-boletos-pj": {
    label: "Recebimentos - Boleto",
    description: "Totais recebidos por boleto, últimos meses.",
  },
  "additional-valor-historico-transacionado-recebidos-boletos-ted-cartao-pj": {
    label: "Recebimentos - Boleto / TED / Cartão",
    description: "Totais recebidos por boleto, TED e cartão, últimos meses.",
  },
  "additional-predicao-recebimento-boleto-pj": {
    label: "Predição de Recebimento",
    description: "Estimativa para o próximo mês.",
  },
  "additional-concentracao-pagamento-pj": {
    label: "Concentração de Pagamentos",
    description: "Dependência de poucos pagadores.",
  },
  "additional-concentracao-recebimento-pj": {
    label: "Concentração de Recebimentos",
    description: "Dependência de poucos clientes.",
  },
  "additional-media-dias-inadimplencia-pj": {
    label: "Média de Dias em Atraso",
    description: "Tempo médio até regularização.",
  },
  "additional-liquidez-recebimento-pj": {
    label: "Saúde Financeira - Recebimento",
    description: "Liquidez focada nos recebíveis.",
  },
  "additional-liquidez-pagamento-pj": {
    label: "Saúde Financeira - Pagamento",
    description: "Liquidez focada nos pagamentos.",
  },
  "additional-score-evolucao-pj": {
    label: "Score de Risco",
    description: "Probabilidade de pagar os boletos nos próximos 90 dias.",
  },
};

const sharedInsumos = [
  "additional-avalia-cadastral-pj",
  "additional-cadastral-pj-plus",
  "additional-avalia-score-positivo-pj",
  "additional-avalia-faturamento-presumido-pj",
  "additional-avalia-faturamento-presumido-pj-plus",
  "additional-avalia-capacidade-pagamento-pj",
  "additional-avalia-protesto-nacional-pj",
  "additional-avalia-historico-transacionado-pj",
  "additional-avalia-liquidez-pj",
];

const financialUrls = [
  "additional-valor-historico-transacionados-pagos-boletos-pj",
  "additional-valor-historico-transacionado-recebidos-boletos-pj",
  "additional-predicao-recebimento-boleto-pj",
  "additional-concentracao-pagamento-pj",
  "additional-concentracao-recebimento-pj",
  "additional-media-dias-inadimplencia-pj",
  "additional-liquidez-recebimento-pj",
  "additional-liquidez-pagamento-pj",
  "additional-valor-historico-transacionado-recebidos-boletos-ted-cartao-pj",
  "additional-valor-historico-transacionados-pagos-boletos-ted-pj",
  "additional-score-evolucao-pj",
];

/** Item compacto (checkbox à esquerda, label à direita) */
function LineCheck({ url, checked, onChange }) {
  const d = defs[url] || {};
  return (
    <div className="flex items-center gap-2">
      <Checkbox
        id={url}
        checked={checked}
        onChange={(e) => onChange(url, e.target.checked)}
        className="scale-90"
      />
      <Label
        htmlFor={url}
        className="text-[13px] font-medium text-slate-800"
        title={d.description || ""}
      >
        {d.label || url}
      </Label>
    </div>
  );
}

export default function AdditionalQueryPJ({
  selectedQueries,
  setSelectedQueries,
  type,
}) {
  const [openModal, setOpenModal] = useState(false);

  // estados por grupo
  const [selectedPrincipais, setSelectedPrincipais] = useState([]);
  const [selectedMax, setSelectedMax] = useState([]);
  const [selectedRisco, setSelectedRisco] = useState([]);
  const [selectedFinancial, setSelectedFinancial] = useState([]);

  // busca o que a empresa realmente tem disponível
  const { data: queryAdditionals = [] } = useQuery(
    ["queryAdditionals", type],
    async () => {
      const { data } = await axios.get(
        `${QUERIES.GET_ADDITIONALS_BY_COMPANY}?type=${type}`
      );
      return data.data;
    }
  );

  const availableUrls = useMemo(
    () => new Set(queryAdditionals.map((q) => q.url)),
    [queryAdditionals]
  );

  // prefill a partir do pai
  useEffect(() => {
    if (!Array.isArray(selectedQueries) || selectedQueries.length === 0) return;
    const has = (u) => selectedQueries.includes(u);

    setSelectedPrincipais(
      ["additional-avalia-max-pj", "additional-avalia-risco-pj"].filter(has)
    );
    setSelectedMax(sharedInsumos.filter(has));
    setSelectedRisco(sharedInsumos.filter(has)); // espelha se o pai trouxe
    setSelectedFinancial(financialUrls.filter(has));
  }, []); // mount only

  // sobe consolidação ao pai
  useEffect(() => {
    const all = Array.from(
      new Set([
        ...selectedPrincipais,
        ...selectedMax,
        ...selectedRisco,
        ...selectedFinancial,
      ])
    );
    setSelectedQueries(all);
  }, [
    selectedPrincipais,
    selectedMax,
    selectedRisco,
    selectedFinancial,
    setSelectedQueries,
  ]);

  /* toggles */
  const togglePrincipal = (id, isChecked) => {
    setSelectedPrincipais((prev) =>
      isChecked ? [...prev, id] : prev.filter((v) => v !== id)
    );

    // regra: marcar Risco garante Protesto Nacional
    if (id === "additional-avalia-risco-pj" && isChecked) {
      if (!selectedRisco.includes("additional-avalia-protesto-nacional-pj")) {
        setSelectedRisco((prev) => [
          ...prev,
          "additional-avalia-protesto-nacional-pj",
        ]);
      }
    }
  };
  const toggleMax = (url, isChecked) =>
    setSelectedMax((prev) =>
      isChecked ? [...prev, url] : prev.filter((v) => v !== url)
    );
  const toggleRisco = (url, isChecked) => {
    let updated = isChecked
      ? [...selectedRisco, url]
      : selectedRisco.filter((v) => v !== url);
    if (
      url === "additional-avalia-risco-pj" &&
      isChecked &&
      !updated.includes("additional-avalia-protesto-nacional-pj")
    ) {
      updated.push("additional-avalia-protesto-nacional-pj");
    }
    setSelectedRisco(updated);
  };
  const toggleFinancial = (url, isChecked) =>
    setSelectedFinancial((prev) =>
      isChecked ? [...prev, url] : prev.filter((v) => v !== url)
    );

  // visíveis pelo que a empresa possui
  const vShared = sharedInsumos.filter((u) => availableUrls.has(u));
  const vFinancial = financialUrls.filter((u) => availableUrls.has(u));
  const show =
    availableUrls.size > 0 && (vShared.length > 0 || vFinancial.length > 0);

  if (!show) return null;

  return (
    <>
      {/* Cabeçalho enxuto */}
      <div className="mb-2 flex items-center gap-2">
        <h3 className="text-sm font-semibold text-slate-900">
          Adicionais de Consulta — PJ
        </h3>
        <AiOutlineQuestionCircle
          className="h-4 w-4 cursor-pointer text-slate-500 hover:text-slate-700"
          onClick={() => setOpenModal(true)}
          title="Como funcionam os adicionais"
        />
      </div>
      <p className="mb-4 text-[12px] text-slate-600">
        Selecione os módulos necessários. Interface compacta, sem distrações.
      </p>

      {/* Linha 1: Pacote Max / Pacote Risco */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {/* Max */}
        <section className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-md">
          <div className="mb-2 flex items-start gap-2">
            <Checkbox
              id="additional-avalia-max-pj"
              checked={selectedPrincipais.includes("additional-avalia-max-pj")}
              onChange={(e) =>
                togglePrincipal("additional-avalia-max-pj", e.target.checked)
              }
              className="scale-95"
            />
            <div className="flex-1 -mt-1">
              <Label
                htmlFor="additional-avalia-max-pj"
                className="text-[14px] font-semibold text-slate-900"
              >
                {defs["additional-avalia-max-pj"].label}
              </Label>
              <div className="text-[11px] text-slate-500">
                {defs["additional-avalia-max-pj"].description}
              </div>
            </div>
          </div>

          {/* lista densa em 2 colunas */}
          <div className="grid grid-cols-1 gap-y-1 sm:grid-cols-2">
            {vShared.map((url) => (
              <LineCheck
                key={`max-${url}`}
                url={url}
                checked={selectedMax.includes(url)}
                onChange={toggleMax}
              />
            ))}
          </div>
        </section>

        {/* Risco */}
        <section className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-md">
          <div className="mb-2 flex items-start gap-2">
            <Checkbox
              id="additional-avalia-risco-pj"
              checked={selectedPrincipais.includes(
                "additional-avalia-risco-pj"
              )}
              onChange={(e) =>
                togglePrincipal("additional-avalia-risco-pj", e.target.checked)
              }
              className="scale-95"
            />
            <div className="flex-1 -mt-1">
              <Label
                htmlFor="additional-avalia-risco-pj"
                className="text-[14px] font-semibold text-slate-900"
              >
                {defs["additional-avalia-risco-pj"].label}
              </Label>
              <div className="text-[11px] text-slate-500">
                {defs["additional-avalia-risco-pj"].description}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-y-1 sm:grid-cols-2">
            {vShared.map((url) => (
              <LineCheck
                key={`risco-${url}`}
                url={url}
                checked={selectedRisco.includes(url)}
                onChange={toggleRisco}
              />
            ))}
          </div>
        </section>
      </div>

      {/* Linha 2: Indicadores Financeiros (ids 35–42) */}
      {vFinancial.length > 0 && (
        <>
          <div className="mt-6 mb-2 flex items-center justify-between">
            <h4 className="text-[12px] font-semibold uppercase tracking-wide text-slate-700">
              Indicadores Financeiros
            </h4>
            <div className="h-px flex-1 bg-gray-400 ml-3" />
          </div>

          <section className="rounded-xl border border-slate-200 bg-white/90 p-3 shadow-md">
            {/* grid 3 colunas no desktop para máxima compactação */}
            <div className="grid grid-cols-1 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
              {vFinancial.map((url) => (
                <LineCheck
                  key={`fin-${url}`}
                  url={url}
                  checked={selectedFinancial.includes(url)}
                  onChange={toggleFinancial}
                />
              ))}
            </div>
          </section>
        </>
      )}

      {/* Modal de ajuda, discreto */}
      <Modal show={openModal} onClose={() => setOpenModal(false)}>
        <ModalHeader>Adicionais — Como usar</ModalHeader>
        <ModalBody>
          <div className="space-y-2 text-sm text-slate-700">
            <p>
              Marque apenas o que agrega valor à sua análise. Os pacotes{" "}
              <strong>Max</strong> e <strong>Risco</strong> permitem combinar
              insumos rapidamente.
            </p>
            <p>
              Ao ativar <strong>Pacote Risco PJ</strong>, o{" "}
              <em>Protesto Nacional</em> é incluído automaticamente para
              garantir o mínimo de cobertura.
            </p>
            <p>
              Em <strong>Indicadores Financeiros</strong>, você encontra
              métricas de boletos, predição, concentração, inadimplência e
              liquidez, de forma objetiva e compacta.
            </p>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
